import React from 'react';
import { Helmet } from 'react-helmet-async';

// Constants for features
const PRODUCT_FEATURES = [
  {
    id: 'automate',
    title: 'Advanced AI Analysis',
    description: 'Deep learning models analyze customer feedback, market trends, and competitive data to uncover unique insights that drive product decisions',
    icon: (
      <path d="M21 8V16C21 18.7614 18.7614 21 16 21H8C5.23858 21 3 18.7614 3 16V8C3 5.23858 5.23858 3 8 3H16C18.7614 3 21 5.23858 21 8Z" stroke="currentColor" strokeWidth="2" fill="currentColor" fillOpacity="0.2" />
    ),
    checkmark: (
      <path d="M17 9L13.9558 12.5662C13.1497 13.4815 12.7466 13.9392 12.2605 14.1742C11.7744 14.4093 11.2256 14.4093 10.7395 14.1742C10.2534 13.9392 9.85035 13.4815 9.04417 12.5662L7 10.25" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    )
  },
  {
    id: 'connect',
    title: 'Customer-Led Insights',
    description: 'AI-powered sentiment analysis and trend detection from customer feedback help you build products that truly resonate with your users',
    icon: (
      <path d="M12 3C16.9706 3 21 7.02944 21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3Z" stroke="currentColor" strokeWidth="2" fill="currentColor" fillOpacity="0.2" />
    ),
    arrow: (
      <>
        <path d="M8.5 12H14.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M12.5 15L15.5 12L12.5 9" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </>
    )
  },
  {
    id: 'save',
    title: 'Smart Automation',
    description: 'Our unique AI models combine deep learning with domain expertise to automate complex product management tasks with unprecedented accuracy',
    icon: (
      <>
        <path d="M5 2H19C20.1046 2 21 2.89543 21 4V6C21 7.10457 20.1046 8 19 8H5C3.89543 8 3 7.10457 3 6V4C3 2.89543 3.89543 2 5 2Z" stroke="currentColor" strokeWidth="2" fill="currentColor" fillOpacity="0.2" />
        <path d="M5 8H19V20C19 21.1046 18.1046 22 17 22H7C5.89543 22 5 21.1046 5 20V8Z" stroke="currentColor" strokeWidth="2" fill="currentColor" fillOpacity="0.2" />
      </>
    ),
    clock: (
      <path d="M12 6V12L15 15" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    )
  }
];

const AiPoweredProductAssistant = () => {
  // Generate structured data for Product schema
  const productSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Strive AI Product Management Assistant",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web-based",
    "description": "Advanced AI-powered product management platform that combines deep learning with domain expertise to analyze customer feedback, market trends, and competitive data for data-driven product decisions.",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "featureList": PRODUCT_FEATURES.map(feature => feature.title).join(", ")
  };

  return (
    <>
      <Helmet>
        <title>AI Product Management Assistant | Strive</title>
        <meta 
          name="description" 
          content="Transform your product management with Strive's advanced AI. Our deep learning models analyze customer feedback and market trends to uncover unique insights that drive product decisions."
        />
        <meta 
          name="keywords" 
          content="AI product management, deep learning, customer feedback analysis, sentiment analysis, market trend analysis, competitive intelligence, customer-led product development"
        />
        <meta property="og:title" content="AI-Powered Product Management Assistant | Strive" />
        <meta 
          property="og:description" 
          content="Revolutionize your product management with advanced AI that goes beyond automation. Get deep insights from customer feedback and market trends to build products that truly resonate."
        />
        <script type="application/ld+json">
          {JSON.stringify(productSchema)}
        </script>
      </Helmet>

      <section 
        id="product-management" 
        className="py-24 bg-gradient-to-br from-blue-50 to-indigo-50 scroll-mt-[8rem]"
        aria-labelledby="product-management-title"
      >
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h1 
                id="product-management-title"
                className="text-4xl font-bold text-gray-900 mb-4"
              >
                Your AI-Powered <span className="text-blue-600">Product Operations Assistant</span>
              </h1>
              
              <p className="text-lg text-gray-700 max-w-3xl mx-auto">
                Strive revolutionizes product operations with advanced AI that goes beyond simple automation. 
                Our deep learning models analyze customer feedback, market trends, and competitive data to uncover 
                unique insights that drive product decisions. Built on a customer-led approach, Strive helps you 
                understand your users' needs and build products that truly resonate.
              </p>
            </div>
            
            <div 
              className="grid grid-cols-1 md:grid-cols-3 gap-8"
              role="list"
              aria-label="Product features"
            >
              {PRODUCT_FEATURES.map((feature) => (
                <div 
                  key={feature.id}
                  className="bg-white rounded-xl shadow-md p-6 border-t-4 border-blue-500 hover:shadow-lg transition-shadow"
                  role="listitem"
                >
                  <div 
                    className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4 mx-auto"
                    aria-hidden="true"
                  >
                    <svg 
                      width="24" 
                      height="24" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      xmlns="http://www.w3.org/2000/svg" 
                      className="text-blue-600"
                      aria-hidden="true"
                    >
                      {feature.icon}
                      {feature.checkmark || feature.arrow || feature.clock}
                    </svg>
                  </div>
                  <h2 className="text-lg font-semibold text-center mb-3 text-gray-800">
                    {feature.title}
                  </h2>
                  <p className="text-gray-600 text-center">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default AiPoweredProductAssistant; 